import { Text, ReactTable, Box } from '@wegroup/design-system';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell } from 'react-table';
import { toEuro } from '../../../../../../sales-conversation/src/utils/stringUtils';
import { CompanyInvestment } from '../../../../types/CompanyScanTypes';
import InfoPopover from '../common/InfoPopover';
import TableActionsMenu from '../common/TableActionsMenu';
import TableFiller from '../common/TableFiller';
import TruncatedHoverText from '../common/TruncatedHoverText';

interface Props {
  companyInvestments: CompanyInvestment[];
  onEdit: (companyInvestment: CompanyInvestment, index: number) => void;
  onRemove: (index: number) => void;
}

const CompanyInvestmentTable: React.FC<React.PropsWithChildren<Props>> = ({
  companyInvestments,
  onEdit,
  onRemove,
}) => {
  const { t } = useTranslation();

  const renderTypeCell = (cell: Cell) => {
    const { type } = cell?.row?.original as CompanyInvestment;

    return <TruncatedHoverText text={type} />;
  };

  const renderDurationCell = (cell: Cell) => {
    const { duration } = cell?.row?.original as CompanyInvestment;

    return <Text color="text.gray">{`${duration} ${t('MONTHS')}`}</Text>;
  };

  const renderAmountCell = (cell: Cell) => {
    const { amount } = cell?.row?.original as CompanyInvestment;

    return <Text color="text.gray">{toEuro(amount)}</Text>;
  };

  const renderPurposeCell = (cell: Cell) => {
    const { purpose } = cell?.row?.original as CompanyInvestment;

    if (!purpose) return '-';

    return (
      <InfoPopover>
        <Text>{purpose}</Text>
      </InfoPopover>
    );
  };

  const renderActionMenuCell = (cell: Cell) => {
    const companyInvestment = cell?.row?.original as CompanyInvestment;
    const index = cell?.row?.index;

    return (
      <TableActionsMenu
        onEdit={() => onEdit(companyInvestment, index)}
        onRemove={() => onRemove(index)}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_TYPE'),
        accessor: 'type',
        Cell: renderTypeCell,
        isSticky: true,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_DURATION'),
        accessor: 'duration',
        Cell: renderDurationCell,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_AMOUNT'),
        accessor: 'amount',
        Cell: renderAmountCell,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_PURPOSE'),
        accessor: 'purpose',
        Cell: renderPurposeCell,
        disableSortBy: true,
      },
      {
        Header: t('ACTIONS'),
        id: 'actions',
        Cell: renderActionMenuCell,
        disableSortBy: true,
      },
    ],
    [companyInvestments],
  );

  return (
    <Box>
      <ReactTable
        data={companyInvestments}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        columns={columns as any}
      />
      {companyInvestments.length === 0 && (
        <TableFiller>
          <Text>
            {t('FLOWS_COMPANY-SCAN_COMPANY-INVESTMENT-INPUT_EMPTY-TABLE')}
          </Text>
        </TableFiller>
      )}
    </Box>
  );
};

export default CompanyInvestmentTable;
